import React, {useRef, useState} from 'react';
import Styled from 'styled-components';
import {
	Alert,
	Button,
	FlexColumn,
	FlexRow,
	Heading,
	Pill,
	PillVariant,
	Text,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {createToast} from '../../../shared/components/toasts/toast';
import {uploadClientAssetFile} from './uploadClientAssetFile';
import {AcknowledgeMutation, ArchiveMutation, commitAsync, requestTicket, UploadVersionMutation} from './clientAssetMutations';
import {formatBytes, statusMessageId, typeMessageId} from './clientAssetPresentation';

type DrawerVersion = {
	id: string;
	versionNo: number;
	sizeBytes: number;
	downloadUrl: string | null | undefined;
	createdAt: string;
	uploadedByFirm: boolean;
	uploadedByEmail: string | null | undefined;
};

/** The slice of a ClientAssetsPage_project asset row the drawer reads. */
export type ClientAssetDrawerAsset = {
	id: string;
	assetType: string;
	name: string;
	status: string;
	archived: boolean;
	versions: ReadonlyArray<DrawerVersion>;
	responses: ReadonlyArray<{
		id: string;
		name: string;
		createdAt: string;
		currentVersion:
			| {downloadUrl: string | null | undefined; uploadedByEmail: string | null | undefined; createdAt: string}
			| null
			| undefined;
	}>;
	readReceipts: ReadonlyArray<{email: string; versionNo: number; readAt: string}>;
};

export type ClientAssetDrawerProps = {
	asset: ClientAssetDrawerAsset;
	/** Relay global id of the project. */
	projectId: string;
	/** Re-runs the page query after a successful action. */
	onChanged: () => void;
	onClose: () => void;
};

const UPLOAD_ERROR_MESSAGE_IDS: Record<string, string> = {
	FILE_TOO_LARGE: 'client_assets.field.file_too_large',
	UPLOAD_FAILED: 'client_assets.error.upload_failed',
};
const TRANSLATION_ID = /^[a-z0-9_]+(\.[a-z0-9_]+)+$/;

/** As in CreateClientAssetModal: UNHANDLED means the framework modal has already explained it. */
const errorMessageIdFor = (error: unknown): string | null => {
	const message = error instanceof Error ? error.message : '';
	if (UPLOAD_ERROR_MESSAGE_IDS[message]) return UPLOAD_ERROR_MESSAGE_IDS[message];
	return TRANSLATION_ID.test(message) ? message : null;
};

const Panel = Styled.aside`
	width: 420px;
	flex: 0 0 420px;
	height: 100%;
	min-height: 100%;
	box-sizing: border-box;
	padding: 24px;
	border-left: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
	background: ${tokensForThemeProvider.sky.sys.color.background.neutral['1']};
	overflow-y: auto;
`;

const Row = Styled.li`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: 8px;
	padding: 6px 0;
`;

const List = Styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
`;

const HiddenFileInput = Styled.input`
	display: none;
`;

const readReceiptLine = (receipt: {email: string; versionNo: number; readAt: string}): string =>
	`${receipt.email} · v${receipt.versionNo} · ${receipt.readAt.slice(0, 10)}`;

/**
 * The firm-side detail view of one client asset: its versions, the client's responses and
 * read receipts, and the actions its state allows. The server re-checks every state guard;
 * hiding a button here only keeps the user from asking for a refusal.
 */
export const ClientAssetDrawer = ({asset, projectId, onChanged, onClose}: ClientAssetDrawerProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const t = (id: string, values?: Record<string, string | number>) => formatMessage({id}, values);

	const [busy, setBusy] = useState(false);
	// A refusal renders inline, translated; the shared warning modal would cover the drawer.
	const [errorMessageId, setErrorMessageId] = useState<string | null>(null);
	// State alone cannot stop a second click landing before the disabled render commits.
	const busyRef = useRef(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const isFirmFile = asset.assetType === 'deliverable' || asset.assetType === 'reference';
	const canUploadVersion = isFirmFile && !asset.archived;
	const canAcknowledge = asset.assetType === 'input_request' && asset.status === 'received' && !asset.archived;
	const canArchive = !asset.archived;
	const versions = [...asset.versions].sort((a, b) => b.versionNo - a.versionNo);

	const toast = (message: string) =>
		createToast({duration: 5000, variant: 'banner', message: <Alert type={Alert.TYPE.SUCCESS} text={message} />});

	/** Runs one action at a time; a failure lands inline and re-enables the buttons. */
	const run = async (action: () => Promise<void>) => {
		if (busyRef.current) return;
		busyRef.current = true;
		setBusy(true);
		setErrorMessageId(null);
		try {
			await action();
		} catch (error) {
			setErrorMessageId(errorMessageIdFor(error));
		} finally {
			busyRef.current = false;
			setBusy(false);
		}
	};

	const handleFile = (file: File) =>
		run(async () => {
			const upload = await uploadClientAssetFile({projectId, file, requestTicket});
			await commitAsync(UploadVersionMutation, {projectId, assetId: asset.id, ...upload});
			onChanged();
		});

	const handleAcknowledge = () =>
		run(async () => {
			await commitAsync(AcknowledgeMutation, {projectId, assetId: asset.id});
			toast(t('client_assets.drawer.acknowledged_toast'));
			onChanged();
		});

	const handleArchive = () => {
		if (busyRef.current) return;
		if (!window.confirm(t('client_assets.drawer.archive_confirm', {name: asset.name}))) return;
		return run(async () => {
			await commitAsync(ArchiveMutation, {projectId, assetId: asset.id});
			toast(t('client_assets.drawer.archived_toast', {name: asset.name}));
			onChanged();
			onClose();
		});
	};

	const downloadLink = (url: string | null | undefined, testId: string) =>
		url ? (
			<a href={url} target="_blank" rel="noopener noreferrer" data-testid={testId}>
				{t('client_assets.drawer.download')}
			</a>
		) : (
			<Text color="auxiliary" size="1">
				{t('client_assets.drawer.download_unavailable')}
			</Text>
		);

	return (
		<Panel data-cy="client-asset-drawer" data-testid="client-asset-drawer">
			<FlexColumn gap="l">
				<FlexRow justifyContent="space-between" alignItems="flex-start">
					<FlexColumn gap="xs">
						<Heading size="4" as="h2">
							{asset.name}
						</Heading>
						<FlexRow gap="s" alignItems="center">
							<Text color="auxiliary">{t(typeMessageId(asset.assetType))}</Text>
							<span data-testid="client-asset-drawer-status">
								<Pill variant={PillVariant.NEUTRAL}>{t(statusMessageId(asset))}</Pill>
							</span>
						</FlexRow>
					</FlexColumn>
					<Button
						emphasis="low"
						icon="close"
						aria-label={t('common.close')}
						onPress={onClose}
						data-cy="client-asset-drawer-close"
					/>
				</FlexRow>

				{errorMessageId && (
					<div data-testid="client-asset-drawer-error" data-cy="client-asset-drawer-error">
						<Alert type={Alert.TYPE.ERROR} text={t(errorMessageId)} />
					</div>
				)}

				<FlexColumn gap="xs">
					<Heading size="4" as="h3">
						{t('client_assets.drawer.versions')}
					</Heading>
					<List data-testid="client-asset-drawer-versions">
						{versions.map(version => (
							<Row key={version.id} data-testid="client-asset-drawer-version">
								<FlexColumn gap="xs">
									<Text>
										{t('client_assets.drawer.version_row', {
											n: version.versionNo,
											date: version.createdAt.slice(0, 10),
											size: formatBytes(version.sizeBytes),
										})}
									</Text>
									<Text color="auxiliary" size="1">
										{version.uploadedByFirm
											? t('client_assets.drawer.uploaded_by_firm')
											: t('client_assets.drawer.uploaded_by_client', {
													email: version.uploadedByEmail ?? '',
											  })}
									</Text>
								</FlexColumn>
								{downloadLink(version.downloadUrl, 'client-asset-drawer-version-download')}
							</Row>
						))}
					</List>
				</FlexColumn>

				{isFirmFile && (
					<FlexColumn gap="xs">
						<Heading size="4" as="h3">
							{t('client_assets.drawer.responses')}
						</Heading>
						{asset.responses.length === 0 ? (
							<Text color="auxiliary">{t('client_assets.drawer.no_responses')}</Text>
						) : (
							<List data-testid="client-asset-drawer-responses">
								{asset.responses.map(response => (
									<Row key={response.id}>
										<FlexColumn gap="xs">
											<Text>{response.name}</Text>
											<Text color="auxiliary" size="1">
												{[
													response.currentVersion?.uploadedByEmail,
													(response.currentVersion?.createdAt ?? response.createdAt).slice(0, 10),
												]
													.filter(Boolean)
													.join(' · ')}
											</Text>
										</FlexColumn>
										{downloadLink(
											response.currentVersion?.downloadUrl,
											'client-asset-drawer-response-download'
										)}
									</Row>
								))}
							</List>
						)}
					</FlexColumn>
				)}

				{asset.assetType === 'reference' && (
					// A plain div rather than the DS FlexColumn, which drops `data-testid`.
					<div data-testid="client-asset-drawer-reads">
						<FlexColumn gap="xs">
							<Heading size="4" as="h3">
								{t('client_assets.drawer.read_receipts')}
							</Heading>
							{asset.readReceipts.length === 0 ? (
								<Text color="auxiliary">{t('client_assets.drawer.no_reads')}</Text>
							) : (
								<List>
									{asset.readReceipts.map(receipt => (
										<Row key={`${receipt.email}-${receipt.versionNo}`}>
											<Text>{readReceiptLine(receipt)}</Text>
										</Row>
									))}
								</List>
							)}
						</FlexColumn>
					</div>
				)}

				<FlexRow gap="s">
					{canUploadVersion && (
						<>
							<HiddenFileInput
								ref={fileInputRef}
								type="file"
								data-testid="client-asset-drawer-file"
								onChange={e => {
									const file = e.target.files?.[0];
									// Reset so picking the same file again still fires a change.
									e.target.value = '';
									if (file) handleFile(file);
								}}
							/>
							<Button
								emphasis="medium"
								disabled={busy}
								onPress={() => fileInputRef.current?.click()}
								data-cy="client-asset-drawer-upload-version"
							>
								{t('client_assets.drawer.upload_new_version')}
							</Button>
						</>
					)}
					{canAcknowledge && (
						<Button
							emphasis="high"
							disabled={busy}
							onPress={handleAcknowledge}
							data-cy="client-asset-drawer-acknowledge"
						>
							{t('client_assets.drawer.acknowledge')}
						</Button>
					)}
					{canArchive && (
						<Button emphasis="low" disabled={busy} onPress={handleArchive} data-cy="client-asset-drawer-archive">
							{t('client_assets.drawer.archive')}
						</Button>
					)}
				</FlexRow>
			</FlexColumn>
		</Panel>
	);
};

export default ClientAssetDrawer;
